"use client";

import { useMemo } from "react";
import { useAuth } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import { apiFetchAuthed } from "../lib/api";
import { queryKeys } from "../lib/query-keys";

type TicketSummary = {
  eventId: string;
  status: string;
};

type TicketsResponse = {
  tickets: TicketSummary[];
  total: number;
};

/**
 * Returns a Set of event IDs the current user holds an active ticket for.
 * Only fires when the user is signed in; returns an empty set otherwise.
 */
export function useMyEventIds(): Set<string> {
  const { isSignedIn, getToken } = useAuth();

  const { data } = useQuery({
    queryKey: queryKeys.users.tickets(),
    queryFn: () =>
      apiFetchAuthed<TicketsResponse>(
        "me/tickets?timeframe=upcoming&limit=100",
        {},
        () => getToken(),
      ),
    enabled: !!isSignedIn,
    staleTime: 2 * 60_000,
  });

  return useMemo(() => {
    if (!data?.tickets) return new Set<string>();
    const active = data.tickets.filter(
      (t) => t.status !== "CANCELLED",
    );
    return new Set(active.map((t) => t.eventId));
  }, [data]);
}
